import { Container, ContainerChild, Graphics, Text } from "pixi.js";
import { getDefaultStore } from "jotai";

import type {
  IStudioWidget,
  IStudioWidgetFollowerGoal,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import { atomAccountAddress } from "../../../../atoms/atomsAccount";
import { overlayEventStatsRepository } from "../../../../infrastructure/Repositories/Overlay/OverlayEventStatsRepository";
import { getFollowerGoalDesign } from "../../../../data/followerGoalDesignsCatalog";
import { FollowerGoalDesignRenderer } from "./FollowerGoalDesignRenderer";

interface WidgetState {
  destination: { x: number; y: number; width: number; height: number };
  options: IStudioWidgetFollowerGoal["options"];
  /**
   * Set ONLY when `options.designId` resolves to a catalog design — the legacy
   * Graphics fields below are then absent and the design sprite is driven via
   * `designRenderer.update(counts)` from `refresh`.
   */
  designRenderer?: FollowerGoalDesignRenderer;
  bg?: Graphics;
  trackBar?: Graphics;
  fillBar?: Graphics;
  labelText?: Text;
  countText?: Text;
  captionText?: Text;
}

const POLL_INTERVAL_MS = 1000;

const parseHex = (input: string | undefined, fallback: number): number => {
  if (!input) return fallback;
  const m = input.match(/^#?([0-9a-f]{6})$/i);
  return m ? parseInt(m[1], 16) : fallback;
};

const parseAlpha = (input: string | undefined): number => {
  if (!input) return 1;
  const rgba = input.match(
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+))?\s*\)$/i,
  );
  if (rgba && rgba[1] !== undefined) return parseFloat(rgba[1]);
  return 1;
};

const fillFromColor = (input: string | undefined, fallback: number) => ({
  color: parseHex(input, fallback),
  alpha: parseAlpha(input),
});

/**
 * Pixi renderer for the Follower Goal widget. Polls the live follow count
 * from `OverlayEventStatsRepository` once per second when in `auto` mode;
 * in `manual` mode it just renders whatever the streamer typed in.
 */
export class FollowerGoalWidgetRenderer {
  private widgets = new Map<string, WidgetState>();
  private timers = new Map<string, ReturnType<typeof setInterval>>();
  private store = getDefaultStore();

  /**
   * Drop any prior state for this widget id — the design renderer (frees its
   * texture) AND the legacy poll timer. Called at the top of every `render` so
   * each re-render starts clean and switching between the design and legacy
   * paths never leaves an orphaned timer or texture.
   */
  private teardownWidget(id: string): void {
    const t = this.timers.get(id);
    if (t) {
      clearInterval(t);
      this.timers.delete(id);
    }
    const prev = this.widgets.get(id);
    if (prev?.designRenderer) prev.designRenderer.destroy();
    this.widgets.delete(id);
  }

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const w = widget as IStudioWidgetFollowerGoal;
    const o = w.options;

    // Start clean: clear whatever this widget rendered last pass (either path).
    this.teardownWidget(widget.id);

    // ── Design-store path ──────────────────────────────────────────────────
    // When `options.designId` resolves to a catalog design, render its CARD SVG
    // (count-driven) as a Pixi sprite at the widget bounds and skip the legacy
    // graphics bar. The shared poll/initial-paint tail below still drives it via
    // `refresh` → `designRenderer.update(counts)`. Absent/unknown ⇒ fall through.
    const design = getFollowerGoalDesign(o.designId);
    if (design) {
      const designRenderer = new FollowerGoalDesignRenderer(design);
      designRenderer.render(container, destination);
      this.widgets.set(widget.id, { destination, options: o, designRenderer });
      void this.refresh(widget.id);
      if (o.currentMode === "auto") {
        const t = setInterval(() => {
          void this.refresh(widget.id);
        }, POLL_INTERVAL_MS);
        this.timers.set(widget.id, t);
      }
      return;
    }

    // Background plate.
    let bg: Graphics | undefined;
    if (o.backgroundColor && o.backgroundColor !== "transparent") {
      bg = new Graphics();
      const fill = fillFromColor(o.backgroundColor, 0x0a0014);
      bg.roundRect(0, 0, destination.width, destination.height, 12).fill({
        color: fill.color,
        alpha: fill.alpha,
      });
      container.addChild(bg);
    }

    // Layout: label row (top), bar (mid), caption (bottom optional).
    const padX = destination.width * 0.05;
    const padY = destination.height * 0.18;
    const usableW = destination.width - padX * 2;
    const labelHeight = destination.height * 0.22;
    const captionHeight = o.caption ? destination.height * 0.18 : 0;
    const barHeight = Math.max(
      8,
      destination.height - padY * 2 - labelHeight - captionHeight,
    );

    const fontFamily = o.fontFamily ?? "Inter, system-ui, sans-serif";
    const fontWeight = o.bold ? "bold" : "500";
    const fontStyle = o.italic ? "italic" : "normal";
    const baseFontSize = o.fontSize ? parseInt(o.fontSize) : 20;
    const labelFontSize = baseFontSize * 0.7;
    const captionFontSize = baseFontSize * 0.6;
    const textColor = o.textColor ?? "#ffffff";

    // Label (left side of the eyebrow row).
    const labelText = new Text({
      text: (o.label || "FOLLOWER GOAL").toUpperCase(),
      style: {
        fontFamily,
        fontSize: labelFontSize,
        fill: textColor,
        fontWeight,
        fontStyle,
        letterSpacing: 1.5,
      },
    });
    labelText.x = padX;
    labelText.y = padY;
    container.addChild(labelText as unknown as ContainerChild);

    // Count (right side of the eyebrow row, updated each tick).
    const countText = new Text({
      text: "0/0",
      style: {
        fontFamily,
        fontSize: labelFontSize,
        fill: textColor,
        fontWeight,
        fontStyle,
        align: "right",
      },
    });
    countText.anchor.set(1, 0);
    countText.x = destination.width - padX;
    countText.y = padY;
    container.addChild(countText as unknown as ContainerChild);

    // Track bar.
    const trackBar = new Graphics();
    const trackY = padY + labelHeight + destination.height * 0.04;
    const track = fillFromColor(o.barBackgroundColor, 0x222222);
    trackBar
      .roundRect(padX, trackY, usableW, barHeight, barHeight / 2)
      .fill({ color: track.color, alpha: track.alpha });
    container.addChild(trackBar);

    // Fill bar (drawn over the track, width updated per tick).
    const fillBar = new Graphics();
    container.addChild(fillBar);

    // Caption.
    let captionText: Text | undefined;
    if (o.caption) {
      captionText = new Text({
        text: o.caption,
        style: {
          fontFamily,
          fontSize: captionFontSize,
          fill: textColor,
          fontWeight: "normal",
          fontStyle,
        },
      });
      captionText.x = padX;
      captionText.y = trackY + barHeight + destination.height * 0.05;
      captionText.alpha = 0.7;
      container.addChild(captionText as unknown as ContainerChild);
    }

    const state: WidgetState = {
      destination,
      options: o,
      bg,
      trackBar,
      fillBar,
      labelText,
      countText,
      captionText,
    };
    this.widgets.set(widget.id, state);

    // Initial paint + start polling for live updates when in auto mode.
    void this.refresh(widget.id);
    if (o.currentMode === "auto") {
      const existing = this.timers.get(widget.id);
      if (existing) clearInterval(existing);
      const t = setInterval(() => {
        void this.refresh(widget.id);
      }, POLL_INTERVAL_MS);
      this.timers.set(widget.id, t);
    }
  }

  private async refresh(widgetId: string): Promise<void> {
    const state = this.widgets.get(widgetId);
    if (!state) return;
    const { options: o, destination } = state;

    let current: number;
    if (o.currentMode === "manual") {
      current = Math.max(0, o.manualCurrent ?? 0);
    } else {
      const accountId = this.store.get(atomAccountAddress);
      if (!accountId) {
        current = 0;
      } else {
        try {
          const res = await overlayEventStatsRepository.getScope(
            accountId,
            "follow",
            o.scope,
            // sessionStartedAt isn't tracked at the renderer level yet —
            // pass undefined so the repo treats the session window as 0.
            undefined,
            o.selectedPlatforms,
          );
          current = res.count;
        } catch (err) {
          console.warn("[FollowerGoalWidget] stats fetch failed", err);
          current = 0;
        }
      }
    }

    const goal = Math.max(1, o.goal ?? 1);

    // ── Design-store path ── push the resolved counts to the sprite renderer
    // (it re-rasterises the chosen design's card only when current/target
    // changes); the legacy Graphics below are absent in this mode.
    if (state.designRenderer) {
      state.designRenderer.update({ current, target: goal });
      return;
    }

    const { fillBar, countText } = state;
    if (!fillBar || !countText) return;

    const ratio = Math.min(1, current / goal);
    const pct = Math.round(ratio * 100);

    const padX = destination.width * 0.05;
    const padY = destination.height * 0.18;
    const usableW = destination.width - padX * 2;
    const captionHeight = o.caption ? destination.height * 0.18 : 0;
    const labelHeight = destination.height * 0.22;
    const barHeight = Math.max(
      8,
      destination.height - padY * 2 - labelHeight - captionHeight,
    );
    const trackY = padY + labelHeight + destination.height * 0.04;

    const bar = fillFromColor(o.barColor, 0x22d3ee);
    fillBar.clear();
    if (ratio > 0) {
      fillBar
        .roundRect(padX, trackY, usableW * ratio, barHeight, barHeight / 2)
        .fill({ color: bar.color, alpha: bar.alpha });
    }

    const showPct = o.showPercent !== false;
    countText.text = showPct
      ? `${current}/${goal} · ${pct}%`
      : `${current}/${goal}`;
  }

  destroy(): void {
    for (const t of this.timers.values()) clearInterval(t);
    this.timers.clear();
    for (const state of this.widgets.values()) {
      if (state.designRenderer) state.designRenderer.destroy();
    }
    this.widgets.clear();
  }
}
